import { Stripe, StripeElements } from "@stripe/stripe-js";
import React, { forwardRef } from "react";
import styled from "styled-components";

import { PaymentRequiredError } from "apis/errors";
import { Button } from "js/Components/Button";
import { StripeRadarError } from "js/controllers/BillingController";
import CheckoutController, { CheckoutControllerState, CheckoutSessionConfirmationError, CheckoutSessionFinalizationTimeoutError } from "js/controllers/CheckoutController";
import getLogger, { LogGroup } from "js/core/logger";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { t } from "js/i18n";
import { ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";

const logger = getLogger(LogGroup.CHECKOUT);

const SubmitButton = styled(Button)`
    width: 100%;
`;

export const SubmissionButtons = CheckoutController.withState(forwardRef<HTMLButtonElement, CheckoutControllerState>(
    function SubmissionButtons({ fetching }, ref) {
        const { submitLabel, progressTitle, progressMessage } = CheckoutController.labels;

        const canCheckout = CheckoutController.canCheckout;

        const handleSubmit = async () => {
            withProgressDialog({
                title: progressTitle,
                message: progressMessage,
                action: async () => {
                    try {
                        await CheckoutController.checkout();
                    } catch (err) {
                        logger.error(err, "CheckoutController.checkout() failed");

                        if (err instanceof PaymentRequiredError || err instanceof CheckoutSessionConfirmationError) {
                            ShowErrorDialog({
                                title: t("There was a problem processing your payment"),
                                message: err.message
                            });
                        } else if (err instanceof StripeRadarError) {
                            ShowErrorDialog({
                                title: t("There was a problem processing your payment"),
                                message: t("We had trouble validating your card; please use a different payment method"),
                            });
                        } else if (err instanceof CheckoutSessionFinalizationTimeoutError) {
                            ShowErrorDialog({
                                title: t("There was a problem upgrading your plan"),
                                message: "Something went wrong, please try again later, we'll take you back to your library.",
                                onClose: () => window.location.href = "/"
                            });
                        } else {
                            ShowErrorDialog({
                                title: t("There was a problem upgrading your plan"),
                                message: err.message
                            });
                        }
                    }
                }
            });
        };

        return (
            <SubmitButton
                ref={ref}
                data-test-id="payment-form-submit-button"
                onClick={handleSubmit}
                disabled={!canCheckout || fetching}
                fullWidth
                blue large
                onKeyDown={(e: React.KeyboardEvent) => {
                    if (canCheckout && !fetching && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        handleSubmit();
                    }
                }}
            >
                {submitLabel}
            </SubmitButton>
        );
    }
));
